import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Users, Bank, ShieldStar, Buildings, Upload, QrCode, X, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABELS = {
  admin:           { label: "Admin",           color: "text-orange-400 border-orange-500/40" },
  secondary_admin: { label: "Secondary Admin", color: "text-orange-300 border-orange-500/30" },
  operator:        { label: "Operator",        color: "text-cyan-300 border-cyan-500/40" },
  fabrication:     { label: "Fabricator",      color: "text-purple-300 border-purple-500/40" },
  delivery:        { label: "Delivery",        color: "text-amber-300 border-amber-500/40" },
  accountant:      { label: "Accountant",      color: "text-emerald-300 border-emerald-500/40" },
  // Legacy label preserved so pre-existing "staff" users still render correctly.
  staff:           { label: "Staff (legacy)",  color: "text-slate-300 border-slate-500/40" },
};

export function UsersManager() {
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("operator");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Edit user state
  const [editing, setEditing] = useState(null);   // full user obj
  const [eName, setEName] = useState("");
  const [eMobile, setEMobile] = useState("");
  const [eRole, setERole] = useState("staff");
  const [ePw, setEPw] = useState("");
  const [eBusy, setEBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !password || password.length < 6) {
      toast.error("Name aur password (min 6 chars) zaruri hain");
      return;
    }
    if (!email.trim() && !mobile.trim()) {
      toast.error("Email ya mobile — kam se kam ek zaruri hai");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", {
        email: email.trim(),
        mobile: mobile.trim(),
        name: name.trim(),
        password,
        role,
      });
      toast.success(`${name} added as ${ROLE_LABELS[role].label}`);
      setName(""); setEmail(""); setMobile(""); setPassword(""); setRole("operator");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setConfirmDelete(null); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEName(u.name || "");
    setEMobile(u.mobile || "");
    setERole(u.role || "staff");
    setEPw("");
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    if (!eName.trim()) return toast.error("Name zaruri hai");
    if (ePw && ePw.length < 6) return toast.error("Password min 6 chars");
    setEBusy(true);
    try {
      const payload = { name: eName.trim(), mobile: eMobile.trim(), role: eRole };
      if (ePw) payload.password = ePw;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setEBusy(false); }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={submit} className="surface p-5 rounded-sm space-y-4" data-testid="add-user-form">
        <div className="text-xs font-mono-num tracking-widest text-slate-400 flex items-center gap-2">
          <Users size={14} /> // ADD STAFF MEMBER
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">FULL NAME</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
              placeholder="Rahul Kumar"
              data-testid="user-name-input"/>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">EMAIL (LOGIN ID) *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
              placeholder="rahul@shobhit.com"
              data-testid="user-email-input"/>
            <div className="text-[9px] font-mono-num tracking-widest text-slate-500">
              *Email YA mobile — kam se kam ek zaruri
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-cyan-300 font-mono-num text-[10px] tracking-widest">MOBILE (ALT LOGIN ID)</Label>
            <Input
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              inputMode="numeric"
              className="bg-slate-800/60 border-cyan-500/40 text-cyan-100 h-10 rounded-sm font-mono-num"
              placeholder="9911223344"
              data-testid="user-mobile-input"/>
            <div className="text-[9px] font-mono-num tracking-widest text-slate-500">
              10-digit — user mobile se bhi login kar sakta hai
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">PASSWORD (MIN 6)</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
              minLength={6}
              data-testid="user-password-input"/>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">ROLE</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-600 text-white">
                <SelectItem value="admin">Admin — full access</SelectItem>
                <SelectItem value="secondary_admin">Secondary Admin — limited (no Reports/Settings/Operators/Recycle)</SelectItem>
                <SelectItem value="operator">Operator — cutting queue only</SelectItem>
                <SelectItem value="fabrication">Fabricator — gate/railing worker portal</SelectItem>
                <SelectItem value="delivery">Delivery — dispatch panel + payment collect</SelectItem>
                <SelectItem value="accountant">Accountant — accounts + payment-in (today only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button type="submit" disabled={busy}
          className="btn-industrial rounded-sm h-11 px-6"
          data-testid="submit-add-user">
          <Plus size={16} className="mr-1" /> {busy ? "ADDING…" : "ADD USER"}
        </Button>
      </form>

      <div className="surface rounded-sm overflow-hidden">
        <div className="px-5 py-3 text-xs font-mono-num tracking-widest text-slate-400 flex items-center gap-2 border-b border-slate-700">
          <ShieldStar size={14} /> // TEAM ({users.length})
        </div>
        <div className="divide-y divide-slate-800">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-5 py-3" data-testid={`user-row-${u.email || u.mobile}`}>
              <div>
                <div className="text-slate-200">{u.name}</div>
                <div className="text-xs font-mono-num text-slate-500 flex flex-wrap items-center gap-x-2">
                  {u.email && <span>{u.email}</span>}
                  {u.email && u.mobile && <span className="text-slate-700">·</span>}
                  {u.mobile && <span className="text-cyan-400">📱 {u.mobile}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {["operator", "staff", "fabrication"].includes(u.role) && (
                  <button
                    onClick={async () => {
                      try {
                        await api.patch(`/users/${u.id}/assistant`, { enabled: !u.assistant_enabled });
                        toast.success(`Hey Shobhit ${!u.assistant_enabled ? "ON" : "OFF"} — ${u.name}`);
                        load();
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    }}
                    className={`text-[10px] font-mono-num px-2 py-1 rounded-sm border ${u.assistant_enabled ? "border-emerald-500/60 text-emerald-300" : "border-slate-700 text-slate-500"}`}
                    data-testid={`assistant-toggle-${u.email || u.mobile}`}
                    title="Hey Shobhit assistant access"
                  >
                    AI {u.assistant_enabled ? "ON" : "OFF"}
                  </button>
                )}
                <span className={`status-pill ${ROLE_LABELS[u.role]?.color}`}>
                  {ROLE_LABELS[u.role]?.label || u.role}
                </span>
                <button
                  onClick={() => openEdit(u)}
                  className="text-slate-500 hover:text-orange-400 p-1"
                  data-testid={`edit-user-${u.email || u.mobile}`}
                  title="Edit user (role, name, mobile, reset password)"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => setConfirmDelete(u)}
                  className="text-slate-500 hover:text-red-400 p-1"
                  data-testid={`delete-user-${u.email}`}
                  title="Remove user"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-white rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This user will no longer be able to log in. Past orders and payments they created stay recorded under their name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-slate-600 text-slate-200 hover:bg-slate-800">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-red-600 hover:bg-red-500 text-white"
              onClick={() => confirmDelete && del(confirmDelete.id)}
              data-testid="confirm-delete-user"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editing && (
        <AlertDialog open={true} onOpenChange={(v) => !v && setEditing(null)}>
          <AlertDialogContent className="bg-slate-900 border-slate-700 text-white rounded-sm max-w-lg" data-testid="edit-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">
                Edit {editing.name}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400 text-xs font-mono-num">
                {editing.email || editing.mobile} · Current role: <b className="text-orange-300">{ROLE_LABELS[editing.role]?.label || editing.role}</b>
                <br />Email login-id change nahi ho sakta. Naya email chahiye toh fresh user banaye.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <form onSubmit={saveEdit} className="space-y-3 pt-2">
              <div>
                <Label className="text-xs text-slate-400 tracking-widest">FULL NAME</Label>
                <Input value={eName} onChange={(e) => setEName(e.target.value)}
                  className="mt-1 bg-slate-800/60 border-slate-600 text-white h-10"
                  data-testid="edit-user-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-400 tracking-widest">MOBILE (ALT LOGIN)</Label>
                <Input value={eMobile} onChange={(e) => setEMobile(e.target.value)}
                  className="mt-1 bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num"
                  placeholder="10 digits — blank = clear"
                  data-testid="edit-user-mobile" />
              </div>
              <div>
                <Label className="text-xs text-slate-400 tracking-widest">ROLE</Label>
                <Select value={eRole} onValueChange={setERole}>
                  <SelectTrigger className="mt-1 bg-slate-800/60 border-slate-600 text-white h-10" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-600 text-white">
                    <SelectItem value="admin">Admin — full access</SelectItem>
                    <SelectItem value="secondary_admin">Secondary Admin — limited</SelectItem>
                    <SelectItem value="operator">Operator</SelectItem>
                    <SelectItem value="fabrication">Fabricator</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                    <SelectItem value="accountant">Accountant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400 tracking-widest">
                  RESET PASSWORD (optional)
                </Label>
                <Input type="password" value={ePw} onChange={(e) => setEPw(e.target.value)}
                  className="mt-1 bg-slate-800/60 border-slate-600 text-white h-10"
                  placeholder="Leave blank to keep existing"
                  data-testid="edit-user-pw" />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel type="button"
                  className="rounded-sm border-slate-600 text-slate-200 hover:bg-slate-800">
                  Cancel
                </AlertDialogCancel>
                <Button type="submit" disabled={eBusy}
                  className="btn-industrial"
                  data-testid="save-edit-user">
                  {eBusy ? "SAVING…" : "SAVE"}
                </Button>
              </AlertDialogFooter>
            </form>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

export function PaymentModesManager() {
  const [modes, setModes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // full mode object being edited
  const [qrMode, setQrMode] = useState(null); // mode we are showing QR for
  const [form, setForm] = useState(defaultBankForm());

  const load = async () => {
    try {
      const [{ data: m }, { data: fa }] = await Promise.all([
        api.get("/settings/payment-modes"),
        api.get("/accounts/financial-accounts").catch(() => ({ data: [] })),
      ]);
      setModes(m);
      setAccounts(fa || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(defaultBankForm());
    setShowAdd(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      key: m.key,
      label: m.label || "",
      bank_name: m.bank_name || "",
      account_number: m.account_number || "",
      account_holder_name: m.account_holder_name || "",
      ifsc_code: m.ifsc_code || "",
      account_type: m.account_type || "savings",
      upi_id: m.upi_id || "",
      customer_payment_disabled: !!m.customer_payment_disabled,
      is_customer_default: !!m.is_customer_default,
      is_delivery_default: !!m.is_delivery_default,
      financial_account_id: m.financial_account_id || "",
    });
    setShowAdd(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const label = form.label.trim();
    if (!label) { toast.error("Display label required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/settings/payment-modes/${editing.key}`, {
          ...form,
          key: editing.key,
          label,
        });
        toast.success(`Updated ${label}`);
      } else {
        const key = (form.key.trim() || label).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
        await api.post("/settings/payment-modes", { ...form, key, label });
        toast.success(`Added ${label}`);
      }
      setShowAdd(false);
      setEditing(null);
      setForm(defaultBankForm());
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (k) => {
    try {
      await api.delete(`/settings/payment-modes/${k}`);
      toast.success("Payment mode removed");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      {/* Add/edit dialog trigger */}
      {!showAdd && (
        <div className="flex justify-end">
          <Button onClick={openAdd} className="btn-industrial rounded-sm h-11 px-6" data-testid="open-add-mode">
            <Plus size={16} className="mr-1" /> ADD BANK ACCOUNT
          </Button>
        </div>
      )}

      {/* Add/edit form */}
      {showAdd && (
        <form onSubmit={submit} className="surface p-5 rounded-sm space-y-4" data-testid="add-mode-form">
          <div className="flex items-center justify-between">
            <div className="text-xs font-mono-num tracking-widest text-slate-400 flex items-center gap-2">
              <Bank size={14} /> {editing ? "EDIT BANK ACCOUNT" : "ADD BANK ACCOUNT"}
            </div>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setEditing(null); }}
              className="text-slate-500 hover:text-white"
              data-testid="cancel-add-mode"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <BankField label="Display Label *" testid="mode-label-input">
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required
                placeholder="e.g. Online — SBI A/c"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" />
            </BankField>
            <BankField label="Bank Name" testid="bank-name-input">
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                placeholder="e.g. State Bank of India"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" />
            </BankField>
            <BankField label="Account Number" testid="account-number-input">
              <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "").slice(0, 20) })}
                placeholder="e.g. 12345678901234"
                inputMode="numeric"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num tracking-wider" />
            </BankField>
            <BankField label="Account Holder Name" testid="holder-name-input">
              <Input value={form.account_holder_name} onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
                placeholder="e.g. Shobhit Creation"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" />
            </BankField>
            <BankField label="IFSC Code" testid="ifsc-input">
              <Input value={form.ifsc_code} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase().slice(0, 11) })}
                placeholder="e.g. SBIN0001234"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num tracking-wider" />
            </BankField>
            <BankField label="Account Type" testid="account-type-select">
              <Select value={form.account_type || "savings"} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  <SelectItem value="savings">Savings</SelectItem>
                  <SelectItem value="current">Current</SelectItem>
                  <SelectItem value="cc">Cash Credit</SelectItem>
                  <SelectItem value="od">Overdraft</SelectItem>
                </SelectContent>
              </Select>
            </BankField>
            <BankField label={<>UPI ID <span className="text-orange-400">(needed for QR)</span></>} testid="upi-id-input" className="md:col-span-2">
              <Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value.trim() })}
                placeholder="e.g. shobhitcreation@okhdfcbank"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num" />
              <div className="text-[10px] font-mono-num text-slate-500 mt-1">
                Customer QR sirf tabhi ban payega jab UPI ID di gayi ho (e.g. name@paytm, name@ybl, name@oksbi)
              </div>
            </BankField>

            {/* Linked Financial Account — single source of truth for balances */}
            <BankField
              label={<>Linked Financial Account <span className="text-cyan-400">(where money lands)</span></>}
              testid="linked-fa-select"
              className="md:col-span-2"
            >
              <Select
                value={form.financial_account_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, financial_account_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" data-testid="linked-fa-trigger">
                  <SelectValue placeholder="— not linked —" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  <SelectItem value="__none__">— not linked —</SelectItem>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.account_name} ({a.account_type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] font-mono-num text-slate-500 mt-1">
                Iss payment method se jitna paisa aayega, wo iss Financial Account me automatically add ho jayega. Duplicate mat banao — same FA multiple modes se link ho sakta hai (SBI UPI + Online SBI → SBI Bank).
              </div>
            </BankField>

            <BankField
              label={<>Customer Online Pay <span className="text-emerald-400">(track page pe dikhega)</span></>}
              testid="customer-payment-toggle"
              className="md:col-span-2"
            >
              <label className="flex items-start gap-3 p-3 border border-slate-700 rounded-sm cursor-pointer hover:border-slate-600 transition-colors">
                <input
                  type="checkbox"
                  checked={!form.customer_payment_disabled}
                  onChange={(e) => setForm({ ...form, customer_payment_disabled: !e.target.checked })}
                  className="mt-0.5 accent-emerald-500"
                  data-testid="customer-payment-enabled"
                />
                <div className="flex-1">
                  <div className="text-sm text-slate-200">
                    {form.customer_payment_disabled
                      ? "Customer ko is UPI ID pe pay karne ka option nahi milega"
                      : "Customer ko is UPI ID pe pay karne ka option track page pe milega"}
                  </div>
                  <div className="text-[10px] font-mono-num text-slate-500 mt-1">
                    UPI ID daali ho tabhi customer page pe dikhega. Owner-only accounts ke liye ye off kar dijiye.
                  </div>
                </div>
              </label>
            </BankField>

            <BankField
              label={<>Customer Default <span className="text-orange-400">(auto-shown QR)</span></>}
              testid="customer-default-toggle-field"
              className="md:col-span-2"
            >
              <label className={
                "flex items-start gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors " +
                (form.is_customer_default
                  ? "border-orange-500/60 bg-orange-500/5"
                  : "border-slate-700 hover:border-orange-500/40")
              }>
                <input
                  type="checkbox"
                  checked={!!form.is_customer_default}
                  onChange={(e) => setForm({ ...form, is_customer_default: e.target.checked })}
                  className="mt-0.5 accent-orange-500"
                  data-testid="customer-default-toggle"
                  disabled={!!form.customer_payment_disabled}
                />
                <div className="flex-1">
                  <div className={"text-sm " + (form.is_customer_default ? "text-orange-200" : "text-slate-200")}>
                    {form.is_customer_default
                      ? "✓ Yeh account customer track page + Delivery Panel ke QR mein use hoga"
                      : "Set as default customer-pay UPI (Delivery QR bhi isi mein banega)"}
                  </div>
                  <div className="text-[10px] font-mono-num text-slate-500 mt-1">
                    Sirf ek UPI ID default ho sakti hai. Delivery boy jab QR generate kare, wahi UPI ID use hogi. Toggle karte hi baaki accounts se default apne aap hat jaayega.
                  </div>
                </div>
              </label>
            </BankField>
            <BankField
              label="Delivery Default"
              full
              testid="delivery-default-field"
              className="md:col-span-2"
            >
              <label className={
                "flex items-start gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors " +
                (form.is_delivery_default
                  ? "border-cyan-500/60 bg-cyan-500/5"
                  : "border-slate-700 hover:border-cyan-500/40")
              }>
                <input
                  type="checkbox"
                  checked={!!form.is_delivery_default}
                  onChange={(e) => setForm({ ...form, is_delivery_default: e.target.checked })}
                  className="mt-0.5 accent-cyan-500"
                  data-testid="delivery-default-toggle"
                />
                <div className="flex-1">
                  <div className={"text-sm " + (form.is_delivery_default ? "text-cyan-200" : "text-slate-200")}>
                    {form.is_delivery_default
                      ? "✓ Delivery Panel ke QR mein SIRF is UPI ID ka use hoga"
                      : "Set as default for Delivery Panel QR"}
                  </div>
                  <div className="text-[10px] font-mono-num text-slate-500 mt-1">
                    Delivery man jab QR generate karega, wahi UPI ID use hogi. Agar set nahi kiya toh Customer-Default UPI ka fallback lega. Sirf ek mode delivery-default ho sakta hai.
                  </div>
                </div>
              </label>
            </BankField>
          </div>

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={busy}
              className="btn-industrial rounded-sm h-11 px-6"
              data-testid="submit-add-mode">
              <Plus size={16} className="mr-1" /> {busy ? "SAVING…" : (editing ? "SAVE CHANGES" : "ADD BANK ACCOUNT")}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setShowAdd(false); setEditing(null); }}
              className="rounded-sm h-11 px-6 border-slate-600 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Configured modes list */}
      <div className="surface rounded-sm overflow-hidden">
        <div className="px-5 py-3 text-xs font-mono-num tracking-widest text-slate-400 border-b border-slate-700">
          // AVAILABLE PAYMENT MODES ({modes.length})
        </div>
        <div className="divide-y divide-slate-800">
          {modes.length === 0 && (
            <div className="px-5 py-6 text-sm text-slate-500 text-center">
              No payment modes configured yet.
            </div>
          )}
          {modes.map((m) => (
            <div key={m.key} className="px-5 py-4" data-testid={`mode-row-${m.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading text-base font-black text-white truncate">{m.label}</div>
                    {m.is_default && (
                      <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 border border-slate-600 rounded-sm text-slate-400">
                        DEFAULT
                      </span>
                    )}
                    {m.upi_id && (
                      <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 border border-emerald-500/40 rounded-sm text-emerald-300">
                        QR READY
                      </span>
                    )}
                    {m.upi_id && !m.customer_payment_disabled && (
                      <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 border border-cyan-500/40 rounded-sm text-cyan-300" title="Customer track page pe pay button dikhega">
                        CUSTOMER PAY
                      </span>
                    )}
                    {m.is_customer_default && (
                      <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 border border-orange-500/60 bg-orange-500/10 rounded-sm text-orange-300" title="Track page ka QR default is UPI ID se banega">
                        ★ TRACK QR
                      </span>
                    )}
                    {m.is_delivery_default && (
                      <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 border border-cyan-500/60 bg-cyan-500/10 rounded-sm text-cyan-300" title="Delivery Panel ka QR is UPI ID se banega">
                        🚚 DELIVERY QR
                      </span>
                    )}
                    {(() => {
                      const linked = accounts.find((a) => a.id === m.financial_account_id);
                      return linked ? (
                        <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 border border-cyan-500/40 rounded-sm text-cyan-200" title="Money lands into this Financial Account" data-testid={`linked-fa-badge-${m.key}`}>
                          → {linked.account_name}
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 border border-red-500/40 rounded-sm text-red-300" title="Ye mode kisi Financial Account se link nahi hai" data-testid={`fa-not-linked-${m.key}`}>
                          ⚠ FA NOT LINKED
                        </span>
                      );
                    })()}
                  </div>
                  {(m.bank_name || m.account_number || m.ifsc_code) && (
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                      {m.bank_name && <Detail label="Bank" value={m.bank_name} />}
                      {m.account_holder_name && <Detail label="A/c Holder" value={m.account_holder_name} />}
                      {m.account_number && <Detail label="A/c No." value={maskAcct(m.account_number)} mono />}
                      {m.ifsc_code && <Detail label="IFSC" value={m.ifsc_code} mono />}
                      {m.account_type && <Detail label="Type" value={m.account_type.toUpperCase()} />}
                      {m.upi_id && <Detail label="UPI" value={m.upi_id} mono />}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {m.upi_id && (
                    <button
                      onClick={() => setQrMode(m)}
                      className="flex items-center gap-1 text-xs font-mono-num tracking-widest text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 px-2.5 py-1.5 rounded-sm"
                      data-testid={`show-qr-${m.key}`}
                      title="Show UPI QR"
                    >
                      <QrCode size={14} /> QR
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(m)}
                    className="text-slate-400 hover:text-white p-1.5 rounded-sm hover:bg-slate-800"
                    data-testid={`edit-mode-${m.key}`}
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  {!m.is_default && (
                    <button
                      onClick={() => del(m.key)}
                      className="text-slate-500 hover:text-red-400 p-1.5 rounded-sm hover:bg-slate-800"
                      title="Remove"
                      data-testid={`delete-mode-${m.key}`}
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* QR dialog */}
      {qrMode && <QrDialog mode={qrMode} onClose={() => setQrMode(null)} />}
    </div>
  );
}

function defaultBankForm() {
  return {
    key: "",
    label: "",
    bank_name: "",
    account_number: "",
    account_holder_name: "",
    ifsc_code: "",
    account_type: "savings",
    upi_id: "",
    customer_payment_disabled: false,
    is_customer_default: false,
    is_delivery_default: false,
    financial_account_id: "",
  };
}

function BankField({ label, children, testid, className = "" }) {
  return (
    <div className={"space-y-1.5 " + className} data-testid={testid ? `field-${testid}` : undefined}>
      <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">
        {typeof label === "string" ? label.toUpperCase() : label}
      </Label>
      {children}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-slate-500 font-mono-num text-[10px] tracking-widest w-20 shrink-0">{label.toUpperCase()}</span>
      <span className={"text-slate-200 truncate " + (mono ? "font-mono-num" : "")}>{value}</span>
    </div>
  );
}

function maskAcct(num) {
  const s = String(num || "");
  if (s.length <= 4) return s;
  return "•••• " + s.slice(-4);
}

/* ---------------- QR Dialog ---------------- */
function QrDialog({ mode, onClose }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busted, setBusted] = useState(0); // increments to bust the img cache

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const qrUrl = `${backendBase}/api/settings/payment-modes/${mode.key}/qr?amount=${encodeURIComponent(amount || 0)}&note=${encodeURIComponent(note)}&_=${busted}`;

  useEffect(() => {
    const t = setTimeout(() => setBusted((b) => b + 1), 250);
    return () => clearTimeout(t);
  }, [amount, note]);

  const download = () => {
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `UPI-QR-${mode.key}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="qr-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// UPI QR CODE</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-qr">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="font-heading text-lg font-black text-white">{mode.label}</div>
            <div className="text-xs font-mono-num text-slate-400">{mode.upi_id}</div>
            {mode.account_holder_name && (
              <div className="text-xs text-slate-500 mt-0.5">Payee: {mode.account_holder_name}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT (₹)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0 = customer decides"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
                data-testid="qr-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (OPTIONAL)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 40))}
                placeholder="e.g. Order #SC-2026-1010"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
                data-testid="qr-note-input"
              />
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 bg-white p-4 rounded-sm">
            <img
              src={qrUrl}
              alt="UPI QR"
              className="w-64 h-64"
              data-testid="qr-image"
            />
            <div className="text-[10px] font-mono-num tracking-widest text-slate-800 font-bold text-center">
              SCAN & PAY WITH ANY UPI APP<br />
              (GPay · PhonePe · Paytm · BHIM)
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={download} className="flex-1 btn-industrial rounded-sm h-10" data-testid="download-qr">
              DOWNLOAD QR
            </Button>
            <Button variant="outline" onClick={onClose} className="rounded-sm h-10 border-slate-600 text-slate-300 hover:bg-slate-800">
              CLOSE
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BusinessSettings() {
  const [form, setForm] = useState({
    business_name: "", tagline: "", address: "", gstin: "", phone: "", email: "",
    website: "", terms: "",
    bank_account_name: "", bank_name: "", bank_account_no: "", bank_ifsc: "", upi_id: "",
  });
  const [logoPath, setLogoPath] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/business");
      setForm({
        business_name: data.business_name || "",
        tagline: data.tagline || "",
        address: data.address || "",
        gstin: data.gstin || "",
        phone: data.phone || "",
        email: data.email || "",
        website: data.website || "",
        terms: data.terms || "",
        bank_account_name: data.bank_account_name || "",
        bank_name: data.bank_name || "",
        bank_account_no: data.bank_account_no || "",
        bank_ifsc: data.bank_ifsc || "",
        upi_id: data.upi_id || "",
      });
      setLogoPath(data.logo_path);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/settings/business", form);
      toast.success("Business info saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/business/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoPath(data.logo_path);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="business-settings-form">
      <div className="surface p-5 rounded-sm space-y-4">
        <div className="text-xs font-mono-num tracking-widest text-slate-400 flex items-center gap-2">
          <Buildings size={14} /> // COMPANY INFO (SHOWN ON ALL PDFs)
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <BField label="Business Name" v={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} testid="bn"/>
          <BField label="Tagline" v={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="tag"/>
          <BField label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} mono testid="ph"/>
          <BField label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="em"/>
          <BField label="Website" v={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="web" placeholder="shobhitfactory.com"/>
          <BField label="GSTIN" v={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} mono testid="gstin"/>
          <BField label="Address" v={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="addr"/>
        </div>
        <BField label="Terms & Conditions" v={form.terms} onChange={(v) => setForm({ ...form, terms: v })}
          multiline placeholder="e.g., Cutting starts only after 50% advance. Balance on delivery." testid="tnc"/>
      </div>

      <div className="surface p-5 rounded-sm space-y-4">
        <div className="text-xs font-mono-num tracking-widest text-slate-400 flex items-center gap-2">
          <Buildings size={14} /> // BANK &amp; UPI (SHOWN ON INVOICE PDF · UPI QR AUTO-GENERATED)
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <BField label="Account Holder Name" v={form.bank_account_name} onChange={(v) => setForm({ ...form, bank_account_name: v })} testid="acname"/>
          <BField label="Bank Name" v={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} testid="bankname" placeholder="HDFC Bank, SBI, etc."/>
          <BField label="Account Number" v={form.bank_account_no} onChange={(v) => setForm({ ...form, bank_account_no: v })} mono testid="acno"/>
          <BField label="IFSC Code" v={form.bank_ifsc} onChange={(v) => setForm({ ...form, bank_ifsc: v.toUpperCase() })} mono testid="ifsc"/>
          <BField label="UPI ID" v={form.upi_id} onChange={(v) => setForm({ ...form, upi_id: v.trim() })} mono testid="upi" placeholder="yourname@upi"/>
        </div>
      </div>

      <div className="surface p-5 rounded-sm">
        <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-3">// LOGO (SHOWN ON INVOICE PDF INSTEAD OF TEXT NAME)</div>
        <div className="flex items-center gap-4 flex-wrap">
          {logoPath ? (
            <div className="w-24 h-24 border border-slate-700 rounded-sm flex items-center justify-center bg-white overflow-hidden p-1">
              <img
                src={`${(import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL)}/api/settings/business/logo?auth=${localStorage.getItem("token") || ""}`}
                alt="Business logo"
                className="max-w-full max-h-full object-contain"
                data-testid="business-logo-preview"
              />
            </div>
          ) : (
            <div className="w-24 h-24 border border-dashed border-slate-700 rounded-sm flex items-center justify-center text-slate-600 text-xs">
              No logo
            </div>
          )}
          <label className="cursor-pointer">
            <input type="file" accept="image/*" hidden
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              data-testid="logo-upload-input"/>
            <span className="inline-flex items-center gap-2 px-4 py-2 border border-slate-600 hover:bg-slate-800 rounded-sm text-sm text-slate-200">
              <Upload size={14}/> {uploading ? "Uploading…" : logoPath ? "Replace Logo" : "Upload Logo"}
            </span>
          </label>
        </div>
      </div>

      <Button type="submit" disabled={busy}
        className="btn-industrial rounded-sm h-11 px-6" data-testid="save-business-button">
        {busy ? "SAVING…" : "SAVE COMPANY INFO"}
      </Button>
    </form>
  );
}

function BField({ label, v, onChange, mono, multiline, placeholder, testid }) {
  const cls = "bg-slate-800/60 border-slate-600 text-white rounded-sm " + (mono ? "font-mono-num " : "");
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">{label.toUpperCase()}</Label>
      {multiline ? (
        <textarea value={v} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} rows={3}
          className={cls + " h-24 w-full px-3 py-2 text-sm border resize-none"}
          data-testid={`business-${testid}`}/>
      ) : (
        <Input value={v} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} className={cls + " h-10"}
          data-testid={`business-${testid}`}/>
      )}
    </div>
  );
}

